// src/pages/reportes/ReportesPage.tsx
import { useState, useMemo } from 'react';
import { Card, Tabs, TabsList, TabsTrigger, TabsContent, Spinner } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Input } from '@/components/ui';
import { BarChart3, Users, Building2, Save } from 'lucide-react';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, startOfQuarter, startOfYear } from 'date-fns';
import { useClientes } from '@/hooks/queries/useClientes';
import { PeriodoReporte } from '@/types/reporte';
import { ReporteOperativo } from './components/ReporteOperativo';
import { ReporteGerencial } from './components/ReporteGerencial';
import { ReporteCliente } from './components/ReporteCliente';
import { ReportesGuardados } from './components/ReportesGuardados';

const PERIODO_LABELS: Record<PeriodoReporte, string> = {
  [PeriodoReporte.HOY]: 'Hoy',
  [PeriodoReporte.AYER]: 'Ayer',
  [PeriodoReporte.SEMANA]: 'Esta semana',
  [PeriodoReporte.SEMANA_PASADA]: 'Semana pasada',
  [PeriodoReporte.MES]: 'Este mes',
  [PeriodoReporte.MES_PASADO]: 'Mes pasado',
  [PeriodoReporte.TRIMESTRE]: 'Este trimestre',
  [PeriodoReporte.ANO]: 'Este año',
  [PeriodoReporte.PERSONALIZADO]: 'Personalizado',
};

function calcularFechasPeriodo(periodo: PeriodoReporte, fechaInicioCustom?: string, fechaFinCustom?: string) {
  const hoy = new Date();
  let fechaInicio: Date;
  let fechaFin: Date;

  switch (periodo) {
    case PeriodoReporte.HOY:
      fechaInicio = hoy;
      fechaFin = hoy;
      break;
    case PeriodoReporte.AYER:
      fechaInicio = subDays(hoy, 1);
      fechaFin = subDays(hoy, 1);
      break;
    case PeriodoReporte.SEMANA:
      fechaInicio = startOfWeek(hoy, { weekStartsOn: 1 });
      fechaFin = endOfWeek(hoy, { weekStartsOn: 1 });
      break;
    case PeriodoReporte.SEMANA_PASADA:
      const semanaAnterior = subWeeks(hoy, 1);
      fechaInicio = startOfWeek(semanaAnterior, { weekStartsOn: 1 });
      fechaFin = endOfWeek(semanaAnterior, { weekStartsOn: 1 });
      break;
    case PeriodoReporte.MES:
      fechaInicio = startOfMonth(hoy);
      fechaFin = endOfMonth(hoy);
      break;
    case PeriodoReporte.MES_PASADO:
      const mesAnterior = subMonths(hoy, 1);
      fechaInicio = startOfMonth(mesAnterior);
      fechaFin = endOfMonth(mesAnterior);
      break;
    case PeriodoReporte.TRIMESTRE:
      fechaInicio = startOfQuarter(hoy);
      fechaFin = hoy;
      break;
    case PeriodoReporte.ANO:
      fechaInicio = startOfYear(hoy);
      fechaFin = hoy;
      break;
    case PeriodoReporte.PERSONALIZADO:
      if (fechaInicioCustom && fechaFinCustom) {
        return { fechaInicio: fechaInicioCustom, fechaFin: fechaFinCustom };
      }
      // Default to last 30 days if custom dates not provided
      fechaInicio = subDays(hoy, 30);
      fechaFin = hoy;
      break;
    default:
      fechaInicio = startOfMonth(hoy);
      fechaFin = endOfMonth(hoy);
  }

  return {
    fechaInicio: format(fechaInicio, 'yyyy-MM-dd'),
    fechaFin: format(fechaFin, 'yyyy-MM-dd'),
  };
}

export function ReportesPage() {
  const [periodo, setPeriodo] = useState<PeriodoReporte>(PeriodoReporte.MES);
  const [fechaInicioCustom, setFechaInicioCustom] = useState<string>('');
  const [fechaFinCustom, setFechaFinCustom] = useState<string>('');
  const [clienteId, setClienteId] = useState<string>('');
  const [activeTab, setActiveTab] = useState('operativo');

  const { data: clientesResponse, isLoading: isLoadingClientes } = useClientes();
  const clientes = clientesResponse?.items ?? [];

  const { fechaInicio, fechaFin } = useMemo(
    () => calcularFechasPeriodo(periodo, fechaInicioCustom, fechaFinCustom),
    [periodo, fechaInicioCustom, fechaFinCustom]
  );

  const showCustomDates = periodo === PeriodoReporte.PERSONALIZADO;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reportes</h1>
          <p className="text-gray-500">Analiza el rendimiento y métricas del equipo</p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Periodo */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Periodo</label>
            <Select value={periodo} onValueChange={(v) => setPeriodo(v as PeriodoReporte)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Seleccionar periodo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PERIODO_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fechas personalizadas */}
          {showCustomDates && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Fecha inicio</label>
                <Input
                  type="date"
                  value={fechaInicioCustom}
                  onChange={(e) => setFechaInicioCustom(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Fecha fin</label>
                <Input
                  type="date"
                  value={fechaFinCustom}
                  onChange={(e) => setFechaFinCustom(e.target.value)}
                  className="w-40"
                />
              </div>
            </>
          )}

          {/* Cliente (solo para pestaña Cliente) */}
          {activeTab === 'cliente' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Cliente</label>
              {isLoadingClientes ? (
                <div className="w-48 h-10 flex items-center justify-center">
                  <Spinner size="sm" />
                </div>
              ) : (
                <Select value={clienteId} onValueChange={setClienteId}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((cliente) => (
                      <SelectItem key={cliente.id} value={cliente.id}>
                        {cliente.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Indicador de periodo seleccionado */}
          {!showCustomDates && (
            <div className="text-sm text-gray-500 pb-2">
              {fechaInicio} - {fechaFin}
            </div>
          )}
        </div>
      </Card>

      {/* Tabs de reportes */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="operativo">
            <BarChart3 className="h-4 w-4 mr-2" />
            Operativo
          </TabsTrigger>
          <TabsTrigger value="gerencial">
            <Users className="h-4 w-4 mr-2" />
            Gerencial
          </TabsTrigger>
          <TabsTrigger value="cliente">
            <Building2 className="h-4 w-4 mr-2" />
            Por Cliente
          </TabsTrigger>
          <TabsTrigger value="guardados">
            <Save className="h-4 w-4 mr-2" />
            Guardados
          </TabsTrigger>
        </TabsList>

        <TabsContent value="operativo">
          <ReporteOperativo
            fechaInicio={fechaInicio}
            fechaFin={fechaFin}
            clienteId={clienteId || undefined}
          />
        </TabsContent>

        <TabsContent value="gerencial">
          <ReporteGerencial periodo={periodo} />
        </TabsContent>

        <TabsContent value="cliente">
          <ReporteCliente clienteId={clienteId} periodo={periodo} />
        </TabsContent>

        <TabsContent value="guardados">
          <ReportesGuardados />
        </TabsContent>
      </Tabs>
    </div>
  );
}
